import { App } from '@gin-betting/core';
import { persistReducer } from '@gin-betting/persist';
import { Record } from 'immutable';

import { SidebarView } from 'app-constants';
import UserDevice from 'services/user-device';
import { Actions } from './actions';
import {
    CHANGE_FAVORITE_COUNT,
    CHANGE_ODD_FORMAT,
    CHANGE_ODDS_STATISTIC,
    CHANGE_SIDEBAR_VIEW,
    CLOSE_FILTER,
    CLOSE_SIDEBAR,
    DISABLE_ODD_HISTORY,
    ENABLE_ODD_HISTORY,
    OPEN_FILTER,
    OPEN_SIDEBAR,
    SET_ACTIVE_MOBILE_COUNTRY_KEY,
    SET_ODDS_CHANGE_MODAL_SHOWN,
} from './constants';
import { UIState, UIStateShape } from './types';

function getInitialState() {
    const UIStateRecord = Record<UIStateShape>(
        {
            oddFormat: App.OddFormat.Decimal,
            sidebarView: UserDevice.getInstance()?.isDeviceMobile()
                ? SidebarView.betslip
                : SidebarView.promo,
            isSidebarOpen: false,
            streamViewerVisibility: 'visible',
            favoriteCount: {
                upcoming: null,
                outrights: null,
            },
            isOddHistoryEnabled: false,
            oddsStatistic: {
                statisticView: App.OddStatisticView.MARKET,
                show: false,
            },
            isFilterOpened: false,
            activeMobileCountryKey: '',
            isOddsChangeModalShown: false,
        },
        'UIState'
    );

    return new UIStateRecord();
}

function reducer(state = getInitialState(), action: Actions): UIState {
    switch (action.type) {
        case OPEN_SIDEBAR: {
            return state.set('isSidebarOpen', true);
        }

        case CLOSE_SIDEBAR: {
            return state.set('isSidebarOpen', false);
        }

        case CHANGE_FAVORITE_COUNT: {
            const { count, type } = action.payload;

            return state.setIn(['favoriteCount', type], count);
        }

        case CHANGE_ODD_FORMAT: {
            const { oddFormat } = action.payload;

            return state.set('oddFormat', oddFormat);
        }

        case CHANGE_SIDEBAR_VIEW: {
            const { sidebarView } = action.payload;

            return state.set('sidebarView', sidebarView);
        }

        case ENABLE_ODD_HISTORY: {
            return state.set('isOddHistoryEnabled', true);
        }

        case DISABLE_ODD_HISTORY: {
            return state.set('isOddHistoryEnabled', false);
        }

        case CHANGE_ODDS_STATISTIC: {
            const { statisticView, show } = action.payload;

            const oddsStatistic = {
                statisticView:
                    statisticView || state.oddsStatistic.statisticView,
                show: show !== undefined ? show : state.oddsStatistic.show,
            };

            return state.set('oddsStatistic', oddsStatistic);
        }

        case OPEN_FILTER: {
            return state.set('isFilterOpened', true);
        }

        case CLOSE_FILTER: {
            return state.set('isFilterOpened', false);
        }

        case SET_ACTIVE_MOBILE_COUNTRY_KEY: {
            return state.set('activeMobileCountryKey', action.payload);
        }

        case SET_ODDS_CHANGE_MODAL_SHOWN: {
            return state.set('isOddsChangeModalShown', action.payload);
        }

        default:
            return state;
    }
}

export { reducer };

export default persistReducer<UIState>(
    {
        key: 'ui',
        stateReconciler: (inboundState, _, reducedState) => {
            return Object.keys(inboundState).reduce((persistedState, key) => {
                if (!reducedState.has(key)) return persistedState;

                return persistedState.set(key, inboundState[key]);
            }, reducedState);
        },
        blacklist: [
            'isSidebarOpen',
            'sidebarView',
            'favoriteCount',
            'matchListView',
            'activeMobileCountryKey',
            'isOddsChangeModalShown',
        ],
    },
    reducer
);
